import { useQuery } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { toast } from '@savgroup-front-common/core/atoms/Toast';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';

import { PaymentService } from '@/api';
import { Kind } from '@/api/Payment/getPaymentSessionQuery';

import messages from '../messages';

import { useGetPurseRedirectionData } from './useGetPurseRedirectionData';

export const GetPaymentSessionQuery = 'GetPaymentSessionQuery';

export const useGetPaymentSession = ({
  kindId,
  kind,
  onConfirmPaymentIntent,
  onCreatePaymentIntent,
}: {
  kindId: string;
  kind: Kind;
  onConfirmPaymentIntent: (payload: Record<never, never>) => void;
  onCreatePaymentIntent: () => void;
}) => {
  const { clientSessionId } = useGetPurseRedirectionData();

  const { data: paymentSessionSummary, isFetching } = useQuery(
    [GetPaymentSessionQuery, { kindId, clientSessionId }],
    async () => {
      if (!clientSessionId) {
        return;
      }

      const response = await PaymentService.getPaymentSessionQuery({
        kindId,
        kind,
        sessionId: clientSessionId,
      });

      if (response.failure) {
        onCreatePaymentIntent();

        if (response.errors[0].code === 'Reverse.Common.Errors.Data.NotFound') {
          return;
        }

        logError(response.errors);

        return;
      }

      if (response.value.isPaymentInProgress) {
        throw new Error('Payment is in progress');
      }

      if (response.value.isPaymentValid) {
        toast.success(safeFormattedIntlString(messages.paymentSuccess));

        onConfirmPaymentIntent({});
      } else {
        toast.error(safeFormattedIntlString(messages.paymentFailure));

        onCreatePaymentIntent();

        return undefined;
      }

      return response.value;
    },
    {
      retry: 20,
      retryDelay: 10_000,
      staleTime: 0,
      suspense: false,
    },
  );

  return { paymentSessionSummary, isLoading: isFetching };
};
